import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, CheckCircle, Sparkles } from 'lucide-react';
import * as Icons from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { productsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChatWidget from '../components/ChatWidget';
import SEO from '../components/SEO';
import { useToast } from '../hooks/use-toast';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { toast } = useToast();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch product from API
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const data = await productsAPI.getById(id);
        setProduct(data);
      } catch (error) {
        console.error('Error fetching product:', error);
        toast({
          title: language === 'id' ? 'Error' : 'Error',
          description: language === 'id' ? 'Produk tidak ditemukan' : 'Product not found',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id, language, toast]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-32 pb-20 px-4 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{language === 'id' ? 'Memuat...' : 'Loading...'}</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <SEO 
          title={language === 'id' ? 'Produk Tidak Ditemukan' : 'Product Not Found'}
          noindex={true}
        />
        <Header />
        <div className="pt-32 pb-20 px-4 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {language === 'id' ? 'Produk Tidak Ditemukan' : 'Product Not Found'}
          </h1>
          <Link to="/">
            <Button className="bg-gradient-to-r from-cyan-500 to-teal-600">
              {language === 'id' ? 'Kembali ke Beranda' : 'Back to Home'}
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const getIconComponent = (iconName) => {
    const IconComponent = Icons[iconName.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join('')];
    return IconComponent || Icons.Box;
  };

  const Icon = getIconComponent(product.icon);
  const isBFSI = product.category === 'bfsi';
  
  // Use product data from API
  const features = product.features || [];
  const benefits = product.benefits || [];
  const technicalSpecs = product.tech_specs || [];

  const handleDownloadBrochure = () => {
    if (product.brochure_url) {
      // If product has specific brochure, download it
      const link = document.createElement('a');
      link.href = product.brochure_url;
      link.download = product.brochure_name || `${product.name_en}-Brochure.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      // Fallback to general brochure
      alert(language === 'id' 
        ? 'Brosur umum akan segera diunduh. Untuk brosur produk spesifik, silakan hubungi kami.' 
        : 'General brochure will be downloaded soon. For product-specific brochure, please contact us.');
    }
  };

  // Product name and description untuk SEO
  const productName = language === 'id' ? product.name_id : product.name_en;
  const productDesc = language === 'id' ? product.description_id : product.description_en;
  
  // Structured data untuk Product
  const productStructuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": productName,
    "description": productDesc,
    "applicationCategory": isBFSI ? "BusinessApplication" : "EnterpriseApplication",
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "price": "0",
      "priceCurrency": "IDR"
    },
    "provider": {
      "@type": "Organization",
      "name": "Manalogi"
    },
    "image": product.image_url || `https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=80`
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={productName}
        description={productDesc}
        keywords={`${productName}, ${isBFSI ? 'BFSI solution' : 'enterprise solution'}, digital transformation, Indonesia`}
        ogImage={product.image_url}
        ogType="product"
        structuredData={productStructuredData}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 via-cyan-50 to-teal-50">
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-200/30 rounded-full blur-3xl -z-10"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-200/30 rounded-full blur-3xl -z-10"></div>

        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-gray-600 hover:text-cyan-600 transition-colors mb-8 group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span>{language === 'id' ? 'Kembali' : 'Back'}</span>
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-gradient-to-r from-cyan-500 to-teal-600">
                {isBFSI 
                  ? (language === 'id' ? 'Produk BFSI' : 'BFSI Product')
                  : (language === 'id' ? 'Produk Industri' : 'Industry Product')
                }
              </Badge>
              
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
                {language === 'id' ? product.name_id : product.name_en}
              </h1>
              
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {language === 'id' ? product.description_id : product.description_en}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                  className="bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700"
                >
                  {language === 'id' ? 'Hubungi Kami' : 'Contact Us'}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={handleDownloadBrochure}
                  className="border-2 border-cyan-600 text-cyan-600 hover:bg-cyan-50"
                >
                  <Download className="mr-2 w-5 h-5" />
                  {language === 'id' ? 'Unduh Brosur' : 'Download Brochure'}
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="relative z-10 bg-white/50 backdrop-blur-sm p-12 rounded-3xl border border-gray-200 shadow-2xl">
                {product.image_url ? (
                  <div className="w-full h-64 rounded-2xl overflow-hidden flex items-center justify-center bg-gray-50">
                    <img 
                      src={product.image_url} 
                      alt={language === 'id' ? product.name_id : product.name_en}
                      className="max-w-full max-h-64 object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-full h-64 bg-gradient-to-br from-cyan-100 to-teal-100 rounded-2xl flex items-center justify-center">
                    <Icon className="w-32 h-32 text-cyan-600" />
                  </div>
                )}
              </div>
              <div className="absolute -bottom-4 -right-4 w-72 h-72 bg-gradient-to-br from-cyan-400/20 to-teal-400/20 rounded-full blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {language === 'id' ? 'Fitur Utama' : 'Key Features'}
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-cyan-500 to-teal-600 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <Card key={feature.id} className="border-2 hover:border-cyan-500 transition-all hover:shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-gradient-to-br from-cyan-100 to-teal-100 rounded-lg flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-cyan-600" />
                      </div>
                    </div>
                    <p className="text-gray-700">
                      {language === 'id' ? feature.text_id : feature.text_en}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-cyan-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {language === 'id' ? 'Manfaat untuk Bisnis Anda' : 'Benefits for Your Business'}
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-cyan-500 to-teal-600 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benefits.map((benefit) => (
              <Card key={benefit.id} className="bg-white hover:shadow-xl transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-xl flex items-center justify-center">
                        <Sparkles className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <p className="text-lg text-gray-700 mt-2">
                      {language === 'id' ? benefit.text_id : benefit.text_en}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {language === 'id' ? 'Spesifikasi Teknis' : 'Technical Specifications'}
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-cyan-500 to-teal-600 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {technicalSpecs.map((spec) => (
              <Card key={spec.id} className="border-2 hover:border-cyan-500 transition-all hover:shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-gradient-to-br from-cyan-100 to-teal-100 rounded-lg flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-cyan-600" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-500 mb-1">
                        {language === 'id' ? spec.key_id : spec.key_en}
                      </p>
                      <p className="font-semibold text-gray-900">
                        {language === 'id' ? spec.value_id : spec.value_en}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-cyan-500 to-teal-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            {language === 'id' 
              ? 'Tertarik dengan Produk Ini?' 
              : 'Interested in This Product?'}
          </h2>
          <p className="text-lg text-white/90 mb-8">
            {language === 'id'
              ? 'Hubungi kami untuk demo gratis dan konsultasi lebih lanjut'
              : 'Contact us for a free demo and further consultation'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/#contact">
              <Button
                size="lg"
                className="bg-white text-cyan-600 hover:bg-gray-100"
              >
                {language === 'id' ? 'Hubungi Kami' : 'Contact Us'}
              </Button>
            </Link>
            <Button
              size="lg"
              variant="outline"
              onClick={handleDownloadBrochure}
              className="border-2 border-white text-white hover:bg-white/10"
            >
              <Download className="mr-2 w-5 h-5" />
              {language === 'id' ? 'Unduh Brosur' : 'Download Brochure'}
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default ProductDetail;
